import { createAction, type Dispatch } from '@reduxjs/toolkit';
import type { AppDispatch, RootState } from '@/app/store';
import { api } from '@/lib/trpc/client';
import type { FormattedPricatProductColor } from '@/server/pricat/pricat-products';
import type { ProductCreationData } from '@/server/assortments/assortment-product-create';
import { PRODUCT_TYPE_TO_FEDAS_TYPE } from '@/lib/helpers/constants-server';

export const b24ProductsSet = createAction<FormattedPricatProductColor[]>('b24-products/set');

export const b24ProductsRequestStarted = createAction('b24-products/request/started');
export const b24ProductsRequestFailed = createAction<string>('b24-products/request/failed');
export const b24ProductsFetchSucceeded = createAction<{
  products: FormattedPricatProductColor[];
  add: boolean;
}>('b24-products/fetch/succeeded');
export const setB24ProductsSearchCanLoadMore = createAction<boolean>(
  'b24-products/set-can-load-more'
);
export const setB24ProductsSearchActiveFilters = createAction<{
  brand: string;
  productType: string;
  productCode: string;
  season: string;
} | null>('b24-products-search/active-filters/save');

export const b24ProductsAddToCreationQueue = createAction<{
  selectedIds: string[];
  productsMappedByProductCode: Record<string, ProductCreationDraft>;
  variantsMappedByProductCode: Record<string, ProductVariationData[]>;
}>('b24-products/add-to-creation-queue');

export const b24ProductsRemoveFromCreationQueue = createAction<{
  selectedIds: string[];
  productsMappedByProductCode: Record<string, ProductCreationDraft>;
  variantsMappedByProductCode: Record<string, ProductVariationData[]>;
}>('b24-products/remove-from-creation-queue');

// eslint-disable-next-line @typescript-eslint/no-empty-object-type
export interface ProductCreationDraft extends Omit<
  ProductCreationData,
  'lookup' | 'eanSapIdMap' | 'optionSelected' | 'sapId'
> {
  // lookup?: ProductCreationData['lookup'];
  // eanSapIdMap?: ProductCreationData['eanSapIdMap'];
  // optionSelected?: ProductCreationData['optionSelected'];
  // sapId?: ProductCreationData['sapId'];
}

export type ProductVariationData = {
  productCode: string;
  colorId: string;
  colorName: string;
  size: string;
  ean: string;
};

export const _b24ProductsAddToCreationQueue =
  (productId: string) => (dispatch: AppDispatch, getState: () => RootState) => {
    const state = getState();

    const b24ProductsState = state.b24Products;
    const { products, creationQueue } = b24ProductsState;

    const { selectedIds, productsMappedByProductCode, variantsMappedByProductCode } = creationQueue;

    // Check if product exists in products
    const product = products.find((p) => p.id === productId);
    // If not, return
    if (!product) {
      return;
    }

    const productCode = product.productCode;
    // Create selectedColor object
    const selectedColor = {
      productCode: productCode,
      colorId: product.colorCode,
      validShipDates: product.validShipDates,
    };

    // Push id to selectedIds
    const newSelectedIds = [...selectedIds, productId];
    // Create copy of productsMappedByProductCode and variantsMappedByProductCode
    const newProductsMappedByProductCode = { ...productsMappedByProductCode };
    const newVariantsMappedByProductCode = { ...variantsMappedByProductCode };

    // Get all variations for product (colors)
    const productColors = products.filter((p) => p.productCode === productCode);

    // Create array of all variations for this product
    const allVariations = productColors.reduce<ProductVariationData[]>((acc, product) => {
      const productVariations =
        product.variations.map((variation) => ({
          productCode: productCode,
          colorId: product.colorCode,
          colorName: product.colorName,
          size: variation.size,
          ean: variation.ean,
        })) || [];
      return [...acc, ...productVariations];
    }, []);

    // Add product to productsMappedByProductCode and variants to newProductsMappedByProductCode
    // Check if product exists in productsMappedByProductCode and newVariantsMappedByProductCode
    const usedEans = newVariantsMappedByProductCode?.[productCode]
      ? newVariantsMappedByProductCode[productCode].map((variant) => variant.ean)
      : [];
    if (
      newProductsMappedByProductCode[productCode] &&
      newVariantsMappedByProductCode[productCode]
    ) {
      // If it does, add only product colors to selectedColors array
      newProductsMappedByProductCode[productCode] = {
        ...newProductsMappedByProductCode[productCode],
        validShipDates: [
          ...newProductsMappedByProductCode[productCode].validShipDates,
          product.validShipDates,
        ],
        selectedColors: [
          ...newProductsMappedByProductCode[productCode].selectedColors,
          selectedColor,
        ],
      };
    } else {
      // Generate product data
      const allColors =
        productColors.map((product) => ({
          productCode,
          colorId: product.colorCode,
          colorName: product.colorName,
          colorImages: product.images,
          validShipDates: product.validShipDates,
        })) || [];

      // Create product object
      const productToAdd = {
        productCode,
        productName: product.productName,
        fedasType: product.fedasType,
        fedasCode: product.fedasCode,
        priceNet: product.priceNet,
        priceRecRetail: product.priceRecRetail,
        country: product.country,
        cnNumber: product.cnNumber,
        gender: product.gender,
        unitOfMeasure: product.unitOfMeasure,
        dimensionInfo: product.dimensionInfo,
        unitsInPackage: product.unitsInPackage,
        validShipDates: [product.validShipDates],
        allColors,
        allVariations,
        selectedColors: [selectedColor],
      };

      // If not, create new product object
      newProductsMappedByProductCode[productCode] = productToAdd;
      // If not, create new variants array
      newVariantsMappedByProductCode[productCode] = [];
    }

    // Add new variations
    for (const variation of allVariations) {
      if (usedEans.includes(variation.ean)) {
        continue;
      }
      usedEans.push(variation.ean);
      newVariantsMappedByProductCode[productCode].push(variation);
    }

    dispatch(
      b24ProductsAddToCreationQueue({
        selectedIds: newSelectedIds,
        productsMappedByProductCode: newProductsMappedByProductCode,
        variantsMappedByProductCode: newVariantsMappedByProductCode,
      })
    );
  };

export const _b24ProductsRemoveFromCreationQueue =
  (productId: string, productCode: string, colorCode: string) =>
  (dispatch: AppDispatch, getState: () => RootState) => {
    const state = getState();

    const b24ProductsState = state.b24Products;
    const { creationQueue } = b24ProductsState;

    const { selectedIds, productsMappedByProductCode, variantsMappedByProductCode } = creationQueue;

    // Remove id from selectedIds
    const newSelectedIds = selectedIds.filter((id) => id !== productId);
    // Create copy of productsMappedByProductCode and variantsMappedByProductCode
    const newProductsMappedByProductCode = { ...productsMappedByProductCode };
    const newVariantsMappedByProductCode = { ...variantsMappedByProductCode };

    // Remove color from productsMappedByProductCode and variants from newVariantsMappedByProductCode
    if (
      newProductsMappedByProductCode[productCode] &&
      newVariantsMappedByProductCode[productCode]
    ) {
      // If it does, add only product colors to selectedColors array
      newProductsMappedByProductCode[productCode] = {
        ...newProductsMappedByProductCode[productCode],
        selectedColors: newProductsMappedByProductCode[productCode].selectedColors.filter(
          (color) => color.productCode !== productCode && color.colorId !== colorCode
        ),
      };
      // Remove product key if selectedColors are empty
      if (!newProductsMappedByProductCode[productCode].selectedColors.length) {
        delete newProductsMappedByProductCode[productCode];
      }
      // If it does, add variants to array
      newVariantsMappedByProductCode[productCode] = newVariantsMappedByProductCode[
        productCode
      ].filter((variant) => variant.colorId !== colorCode && variant.productCode !== productCode);
      // Remove product key if variants are empty
      if (!newVariantsMappedByProductCode[productCode].length) {
        delete newVariantsMappedByProductCode[productCode];
      }
    }

    dispatch(
      b24ProductsRemoveFromCreationQueue({
        selectedIds: newSelectedIds,
        productsMappedByProductCode: newProductsMappedByProductCode,
        variantsMappedByProductCode: newVariantsMappedByProductCode,
      })
    );
  };

export const b24ProductsFetch =
  (lastId?: string) => (dispatch: Dispatch, getState: () => RootState) => {
    dispatch(b24ProductsRequestStarted());

    const state = getState();

    const b24SearchState = state.b24ProductsSearch;
    const { activeFilters } = b24SearchState;

    if (
      !activeFilters.brand ||
      !activeFilters.productType ||
      (!activeFilters.season && activeFilters.brand !== 'TEST')
    ) {
      return;
    }

    const requestParams: {
      brand: string;
      fedasType: string;
      season?: string[];
      modelNo?: string;
      lastId?: string;
    } = {
      brand: activeFilters.brand,
      fedasType:
        PRODUCT_TYPE_TO_FEDAS_TYPE[
          activeFilters.productType as keyof typeof PRODUCT_TYPE_TO_FEDAS_TYPE
        ],
    };

    if (activeFilters.season) {
      const year = activeFilters.season.split('/')[1];
      requestParams.season = [activeFilters.season, `NOS/${year}`];
    }

    if (activeFilters.productCode) {
      // Trim every productCode and create single string
      requestParams.modelNo = activeFilters.productCode
        .split(',')
        .map((code) => code.trim())
        .join(',');
    }

    if (lastId) {
      requestParams.lastId = lastId;
    }

    return api.b24.getPricatProducts.query(requestParams).then(
      (products) => {
        dispatch(
          b24ProductsFetchSucceeded({
            products: products || [],
            add: false, //!!lastId, // Pagination is not implemented
          })
        );
        dispatch(
          setB24ProductsSearchCanLoadMore(false)
        ); /*!!(products?.length && products.length === 100)*/
      },
      ({ message }) => dispatch(b24ProductsRequestFailed(message))
    );
  };
